import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { StatCard } from '../components/dashboard/StatCard'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Skeleton } from '../components/ui/skeleton'
import { Button } from '../components/ui/button'
import {
  TrendingUp,
  Receipt,
  Percent,
  MinusCircle,
  Wallet,
  FileText,
  ArrowRight,
  Plus,
} from 'lucide-react'
import { useInvoicesList } from '../hooks/useInvoices'
import { useIncomingInvoicesList } from '../hooks/useIncomingInvoices'
import { Invoice } from '../types/invoice'
import { IncomingInvoice } from '../types/incomingInvoice'

const statusKeys: Record<string, string> = {
  draft: 'invoices.status.draft',
  created: 'invoices.status.created',
  annulled: 'invoices.status.annulled',
  corrected: 'invoices.status.corrected',
  paid: 'invoices.status.paid',
  partially_paid: 'invoices.status.partiallyPaid',
}

const statusVariants: Record<string, 'secondary' | 'warning' | 'success' | 'destructive' | 'default'> = {
  draft: 'secondary',
  created: 'warning',
  annulled: 'destructive',
  corrected: 'default',
  paid: 'success',
  partially_paid: 'warning',
}

function isActiveInvoice(inv: Invoice) {
  return inv.status !== 'annulled' && inv.status !== 'corrected'
}

function isActiveIncoming(inv: IncomingInvoice) {
  return inv.status !== 'annulled' && inv.status !== 'corrected'
}

function isCurrentMonth(dateStr: string) {
  const d = new Date(dateStr)
  const now = new Date()
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
}

function isPreviousMonth(dateStr: string) {
  const d = new Date(dateStr)
  const now = new Date()
  const prevMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1
  const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
  return d.getFullYear() === prevYear && d.getMonth() === prevMonth
}

function isCurrentQuarter(dateStr: string) {
  const d = new Date(dateStr)
  const now = new Date()
  return d.getFullYear() === now.getFullYear() && Math.floor(d.getMonth() / 3) === Math.floor(now.getMonth() / 3)
}

export function Dashboard() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: outgoingInvoices = [], isLoading: loadingOut } = useInvoicesList({ limit: 100 })
  const { data: incomingInvoices = [], isLoading: loadingIn } = useIncomingInvoicesList({ limit: 100 })

  const isLoading = loadingOut || loadingIn

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const stats = useMemo(() => {
    const activeOut = outgoingInvoices.filter(isActiveInvoice)
    const activeIn = incomingInvoices.filter(isActiveIncoming)

    // Current quarter totals (outgoing)
    const quarterOut = activeOut.filter(inv => isCurrentQuarter(inv.date))
    const turnover = quarterOut.reduce((sum, inv) => sum + inv.baseAmount, 0)
    const vatCollected = quarterOut.reduce((sum, inv) => sum + inv.totalVat, 0)
    const withholdings = quarterOut.reduce((sum, inv) => sum + inv.totalWithholding, 0)

    // Current quarter incoming (deductible VAT)
    const quarterIn = activeIn.filter(inv => isCurrentQuarter(inv.date))
    const totalExpenses = quarterIn.reduce((sum, inv) => sum + inv.totalAmount, 0)

    // Net VAT to pay (simplified: collected - no deductible info available from incoming)
    const netVat = vatCollected

    // Current month vs previous month (outgoing)
    const currentMonthOut = activeOut.filter(inv => isCurrentMonth(inv.date))
    const prevMonthOut = activeOut.filter(inv => isPreviousMonth(inv.date))
    const currentMonthTotal = currentMonthOut.reduce((sum, inv) => sum + inv.totalAmount, 0)
    const prevMonthTotal = prevMonthOut.reduce((sum, inv) => sum + inv.totalAmount, 0)
    const monthTrend = prevMonthTotal > 0
      ? ((currentMonthTotal - prevMonthTotal) / prevMonthTotal) * 100
      : 0

    // Pending to collect
    const pendingToCollect = activeOut
      .filter(inv => inv.status === 'created' || inv.status === 'partially_paid')
      .reduce((sum, inv) => sum + inv.totalAmount, 0)

    // Recent invoices (last 5)
    const recentInvoices = [...activeOut]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5)

    return {
      turnover,
      vatCollected,
      withholdings,
      netVat,
      totalExpenses,
      pendingToCollect,
      monthTrend,
      prevMonthTotal,
      recentInvoices,
      totalOutgoing: activeOut.length,
      totalIncoming: activeIn.length,
    }
  }, [outgoingInvoices, incomingInvoices])

  const currentQuarterLabel = () => {
    const now = new Date()
    const q = Math.floor(now.getMonth() / 3) + 1
    return `Q${q} ${now.getFullYear()}`
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('dashboard.title')}</h1>
          <p className="text-muted-foreground">
            {t('dashboard.overview')} — {currentQuarterLabel()}
          </p>
        </div>
        <Button onClick={() => navigate('/dashboard/invoices/create')}>
          <Plus className="mr-2 h-4 w-4" />
          {t('invoices.newInvoice')}
        </Button>
      </div>

      {/* Main financial stats */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[120px]" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title={t('dashboard.turnover')}
            value={formatCurrency(stats.turnover)}
            description={currentQuarterLabel()}
            icon={TrendingUp}
            trend={stats.prevMonthTotal > 0 ? {
              value: Math.round(stats.monthTrend * 10) / 10,
              isPositive: stats.monthTrend >= 0,
            } : undefined}
          />
          <StatCard
            title={t('dashboard.vatCollected')}
            value={formatCurrency(stats.vatCollected)}
            description={t('dashboard.quarterLabel')}
            icon={Percent}
          />
          <StatCard
            title={t('dashboard.withholdings')}
            value={formatCurrency(stats.withholdings)}
            description={t('dashboard.quarterLabel')}
            icon={MinusCircle}
          />
          <StatCard
            title={t('dashboard.pendingToCollect')}
            value={formatCurrency(stats.pendingToCollect)}
            description={t('dashboard.pendingInvoices')}
            icon={Wallet}
          />
        </div>
      )}

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.netVat')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <>
                <p className="text-2xl font-bold">{formatCurrency(stats.netVat)}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('dashboard.vatToPayDesc')}</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.invoicesIssued')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="flex items-baseline gap-3">
                  <p className="text-2xl font-bold">{stats.totalOutgoing}</p>
                  <span className="text-sm text-muted-foreground">{t('dashboard.outgoing')}</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.expenses')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalExpenses)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.totalIncoming} {t('dashboard.incomingInvoices')}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Invoices */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              {t('dashboard.recentInvoices')}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/invoices')}>
              {t('dashboard.viewAll')}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : stats.recentInvoices.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <FileText className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">{t('invoices.noInvoices')}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => navigate('/dashboard/invoices/create')}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('invoices.createInvoice')}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {stats.recentInvoices.map(inv => (
                <div
                  key={inv.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => navigate(`/dashboard/invoices/${inv.id}`)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{inv.invoiceNumber}</p>
                        <Badge variant={statusVariants[inv.status] as any} className="shrink-0">
                          {t(statusKeys[inv.status])}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {inv.recipient.name} — {formatDate(inv.date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="text-sm font-semibold">{formatCurrency(inv.totalAmount)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
